/**
 * Sentry Integration Scaffold + P8.1 ops_incidents fan-in
 *
 * Optional Sentry DSN (`VITE_SENTRY_DSN`). Regardless of DSN, production
 * exceptions from verified ordinary identities can also be reported to
 * `report-client-error` → `ops_incidents`
 * (deduped daily fingerprint). Never pages Discord for client noise.
 *
 * @example
 * // In main.tsx or App.tsx:
 * import { initSentry } from '@/lib/sentry';
 * initSentry();
 */

import { logger } from './observability';
import { supabase } from '@/integrations/supabase/client';

interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

interface SentryUser {
  id: string;
  email?: string;
  role?: string;
  organizationId?: string;
}

// Check if Sentry is configured
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const IS_PRODUCTION = import.meta.env.PROD;
const IS_SENTRY_ENABLED = !!SENTRY_DSN;

/** In-session dedupe so one crash loop does not flood the edge. */
const reportedFingerprints = new Set<string>();
const MAX_SESSION_REPORTS = 20;
let pendingOpsReports = 0;

/**
 * Initialize Sentry SDK
 * Call this once at app startup (main.tsx)
 */
export function initSentry(): void {
  if (!IS_SENTRY_ENABLED) {
    if (IS_PRODUCTION) {
      logger.warn('Sentry DSN not configured. Verified ordinary client errors can fan-in to ops_incidents.', {
        hint: 'Native, signed-out and unverifiable sessions do not submit remote error reports.',
      });
    }
    return;
  }

  // When Sentry SDK is added, initialize here:
  // Sentry.init({
  //   dsn: SENTRY_DSN,
  //   environment: IS_PRODUCTION ? 'production' : 'development',
  //   tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0,
  //   replaysSessionSampleRate: 0.1,
  //   replaysOnErrorSampleRate: 1.0,
  //   integrations: [
  //     Sentry.browserTracingIntegration(),
  //     Sentry.replayIntegration(),
  //   ],
  // });

  logger.info('Sentry initialized', { environment: IS_PRODUCTION ? 'production' : 'development' });
}

/**
 * Set the current user context for Sentry
 */
export function setSentryUser(user: SentryUser | null): void {
  if (!IS_SENTRY_ENABLED) return;

  if (user) {
    // Sentry.setUser({
    //   id: user.id,
    //   email: user.email,
    //   role: user.role,
    // });
    // Sentry.setTag('organization_id', user.organizationId);
    logger.debug('Sentry user context set', { userId: user.id, role: user.role });
  } else {
    // Sentry.setUser(null);
    logger.debug('Sentry user context cleared');
  }
}

function fingerprintOf(error: Error, context?: Record<string, unknown>): string {
  const component = typeof context?.componentName === 'string' ? context.componentName : '';
  const href = typeof window !== 'undefined' ? window.location.pathname : '';
  return `${error.name}|${error.message}|${component}|${href}`;
}

/**
 * Fire-and-forget client error → ops_incidents (P8.1).
 * Skipped in local DEV unless `forceOpsBus` is set on context.
 * Native, absent, unknown and changed identities do not send a report.
 * forceOpsBus never bypasses the identity gate.
 */
async function reportToOpsBus(error: Error, errorId: string, context?: Record<string, unknown>): Promise<void> {
  const force = Boolean(context?.forceOpsBus);
  if (!IS_PRODUCTION && !force) return;
  if (reportedFingerprints.size + pendingOpsReports >= MAX_SESSION_REPORTS) return;

  pendingOpsReports++;
  try {
    // Skip native/signed-out sessions locally before any Auth verification call.
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (sessionError || session?.user?.is_anonymous !== false ||
        typeof session.access_token !== 'string' || !session.access_token.trim() ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(session.user.id)) return;
    const { data, error: authError } = await supabase.auth.getUser(session.access_token);
    const user = data?.user;
    if (authError || user?.is_anonymous !== false || user.id !== session.user.id) return;
    const { data: currentData, error: currentError } = await supabase.auth.getSession();
    const current = currentData?.session;
    if (currentError || current?.access_token !== session.access_token ||
        current.user?.id !== user.id || current.user.is_anonymous !== false) return;

    if (reportedFingerprints.size >= MAX_SESSION_REPORTS) return;
    const fp = fingerprintOf(error, context);
    if (reportedFingerprints.has(fp)) return;
    reportedFingerprints.add(fp);

    const component =
      typeof context?.componentName === 'string'
        ? context.componentName
        : typeof context?.component === 'string'
          ? context.component
          : undefined;

    const pathwayId =
      typeof context?.pathway_id === 'string'
        ? context.pathway_id
        : typeof context?.pathwayId === 'string'
          ? context.pathwayId
          : undefined;

    await supabase.functions
      .invoke('report-client-error', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          message: error.message || String(error),
          name: error.name || 'Error',
          stack: error.stack?.slice(0, 2000),
          component,
          href: typeof window !== 'undefined' ? window.location.href : undefined,
          error_id: errorId,
          pathway_id: pathwayId,
          context: {
            ...Object.fromEntries(
              Object.entries(context ?? {}).filter(
                ([k]) => !['forceOpsBus', 'componentStack'].includes(k),
              ),
            ),
          },
        },
      });
  } catch {
    /* Unknown identity and reporting failure must not emit raw diagnostics. */
  } finally {
    pendingOpsReports--;
  }
}

/**
 * Capture an exception and send to Sentry (when configured) + ops_incidents (prod).
 */
export function captureException(error: Error, context?: Record<string, unknown>): string {
  const errorId = logger.captureError(error, context);
  void reportToOpsBus(error, errorId, context);

  if (!IS_SENTRY_ENABLED) {
    return errorId;
  }

  // const sentryId = Sentry.captureException(error, {
  //   extra: context,
  //   tags: { errorId },
  // });
  // return sentryId;

  return errorId;
}

/**
 * Capture a message for debugging
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!IS_SENTRY_ENABLED) {
    logger[level === 'warning' ? 'warn' : level](message);
    return;
  }

  // Sentry.captureMessage(message, level);
  logger[level === 'warning' ? 'warn' : level](message);
}

/**
 * Add breadcrumb for debugging trail
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  if (!IS_SENTRY_ENABLED) return;

  // Sentry.addBreadcrumb({
  //   category,
  //   message,
  //   data,
  //   level: 'info',
  // });
}

/**
 * Start a performance transaction
 */
export function startTransaction(name: string, op: string): { finish: () => void } {
  if (!IS_SENTRY_ENABLED) {
    return { finish: () => {} };
  }

  // const transaction = Sentry.startTransaction({ name, op });
  // return { finish: () => transaction.finish() };

  return { finish: () => {} };
}

/**
 * Set a tag for all subsequent events
 */
export function setTag(key: string, value: string): void {
  if (!IS_SENTRY_ENABLED) return;

  // Sentry.setTag(key, value);
}

/**
 * Check if Sentry is configured and enabled
 */
export function isSentryEnabled(): boolean {
  return IS_SENTRY_ENABLED;
}

// Export config for reference
export const sentryConfig: Partial<SentryConfig> = {
  dsn: SENTRY_DSN,
  environment: IS_PRODUCTION ? 'production' : 'development',
  tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0,
};
